import { useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Card, Button, Space, Spin, Tag, Modal, Form, Input, InputNumber, Select, Radio } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { PageHeader } from '../../components/common/PageHeader';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ErrorResult } from '../../components/common/ErrorResult';
import { useSubscriptionDetail, useUpgradeSubscription, useCancelSubscription, useRefundSubscription, useExtendTrial, useChangeSubscriptionStatus } from '../../hooks/useSubscriptions';
import { usePermissions } from '../../hooks/usePermissions';
import { SUBSCRIPTION_STATUS_COLORS, PLAN_LABELS } from '../../lib/constants';
import dayjs from 'dayjs';

export function SubscriptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: sub, isLoading, isError, refetch } = useSubscriptionDetail(id!);
  const { canManageSubscriptions } = usePermissions();
  const upgrade = useUpgradeSubscription();
  const cancel = useCancelSubscription();
  const refund = useRefundSubscription();
  const extendTrial = useExtendTrial();
  const changeStatus = useChangeSubscriptionStatus();
  const [modal, setModal] = useState<string | null>(null);
  const [form] = Form.useForm();

  if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (isError || !sub) return <ErrorResult onRetry={refetch} />;

  const handleAction = () => {
    form.validateFields().then((values) => {
      const actions: Record<string, () => void> = {
        upgrade: () => upgrade.mutate({ id: id!, data: { new_plan_id: values.new_plan_id, reason: values.reason } }),
        cancel: () => cancel.mutate({ id: id!, data: { reason: values.reason, immediate: values.immediate } }),
        refund: () => refund.mutate({ id: id!, data: { amount: values.amount, reason: values.reason, refund_type: values.refund_type } }),
        extend_trial: () => extendTrial.mutate({ id: id!, data: { days: values.days, reason: values.reason } }),
        change_status: () => changeStatus.mutate({ id: id!, data: { status: values.status, reason: values.reason } }),
      };
      actions[modal!]?.();
      setModal(null);
      form.resetFields();
    });
  };

  return (
    <>
      <PageHeader title={`${sub.organization_name} Subscription`} extra={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/subscriptions')}>Back</Button>} />
      <Card>
        <Descriptions column={2}>
          <Descriptions.Item label="Plan"><Tag>{PLAN_LABELS[sub.plan_id] || sub.plan_id}</Tag></Descriptions.Item>
          <Descriptions.Item label="Status"><StatusBadge status={sub.status} colorMap={SUBSCRIPTION_STATUS_COLORS} /></Descriptions.Item>
          <Descriptions.Item label="Billing Cycle">{sub.billing_cycle}</Descriptions.Item>
          <Descriptions.Item label="Owner">{sub.owner_email}</Descriptions.Item>
          <Descriptions.Item label="Period Start">{sub.current_period_start ? dayjs(sub.current_period_start).format('MMM D, YYYY') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Period End">{sub.current_period_end ? dayjs(sub.current_period_end).format('MMM D, YYYY') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Trial End">{sub.trial_end ? dayjs(sub.trial_end).format('MMM D, YYYY') : '-'}</Descriptions.Item>
          <Descriptions.Item label="Cancel at Period End">{sub.cancel_at_period_end ? 'Yes' : 'No'}</Descriptions.Item>
          <Descriptions.Item label="Stripe Sub ID">{sub.stripe_subscription_id || '-'}</Descriptions.Item>
          <Descriptions.Item label="Stripe Customer ID">{sub.stripe_customer_id || '-'}</Descriptions.Item>
          {sub.admin_notes && <Descriptions.Item label="Admin Notes" span={2}>{sub.admin_notes}</Descriptions.Item>}
        </Descriptions>
      </Card>
      {canManageSubscriptions && (
        <Card title="Actions" style={{ marginTop: 16 }}>
          <Space wrap>
            <Button type="primary" onClick={() => setModal('upgrade')}>Upgrade</Button>
            <Button onClick={() => setModal('change_status')}>Change Status</Button>
            <Button onClick={() => setModal('extend_trial')}>Extend Trial</Button>
            <Button danger onClick={() => setModal('cancel')}>Cancel</Button>
            <Button danger onClick={() => setModal('refund')}>Refund</Button>
          </Space>
        </Card>
      )}
      <Modal title={modal?.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())} open={!!modal} onOk={handleAction} onCancel={() => { setModal(null); form.resetFields(); }} okText="Confirm">
        <Form form={form} layout="vertical">
          {modal === 'change_status' && <Form.Item name="status" label="New Status" rules={[{ required: true }]} initialValue={sub?.status}><Select options={[{ label: 'Trialing', value: 'trialing' }, { label: 'Active', value: 'active' }, { label: 'Past Due', value: 'past_due' }, { label: 'Canceled', value: 'canceled' }, { label: 'Unpaid', value: 'unpaid' }, { label: 'Incomplete', value: 'incomplete' }, { label: 'Incomplete Expired', value: 'incomplete_expired' }, { label: 'Expired', value: 'expired' }]} /></Form.Item>}
          {modal === 'upgrade' && <Form.Item name="new_plan_id" label="New Plan" rules={[{ required: true }]}><Select options={['starter', 'growth', 'pro', 'business'].map((p) => ({ label: PLAN_LABELS[p], value: p }))} /></Form.Item>}
          {modal === 'cancel' && <Form.Item name="immediate" label="When" initialValue={false}><Radio.Group><Radio value={false}>At Period End</Radio><Radio value={true}>Immediately</Radio></Radio.Group></Form.Item>}
          {modal === 'refund' && <><Form.Item name="amount" label="Amount ($)" rules={[{ required: true }]}><InputNumber min={0.01} step={0.01} style={{ width: '100%' }} /></Form.Item><Form.Item name="refund_type" label="Type" initialValue="partial"><Radio.Group><Radio value="full">Full</Radio><Radio value="partial">Partial</Radio></Radio.Group></Form.Item></>}
          {modal === 'extend_trial' && <Form.Item name="days" label="Days" rules={[{ required: true }]}><InputNumber min={1} max={90} style={{ width: '100%' }} /></Form.Item>}
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </>
  );
}
